import { signOut, updateProfile } from 'firebase/auth';
import { doc, Timestamp, updateDoc } from 'firebase/firestore';
import {
    Activity,
    Building,
    Calendar,
    Camera,
    CheckCircle,
    Clock,
    Edit2,
    LogOut,
    Mail,
    Phone,
    Save,
    Shield,
    TrendingUp,
    X
} from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/header/Navbar';
import { useAuth } from '../hooks/useAuth';
// import { useComplaintsCount } from '../hooks/useComplaintsCount';
import { auth, db } from '../network/firebaseConfig';

const Profile = () => {
    const navigate = useNavigate();
    const { user, refreshUser } = useAuth();
    // const counts = useComplaintsCount();
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        department: '',
        role: '',
        avatarUrl: ''
    });
    const [userStats, setUserStats] = useState({
        totalComplaints: 0,
        resolvedComplaints: 0,
        pendingComplaints: 0,
        avgResolutionTime: 0
    });

    useEffect(() => {
        if (user) {
            setFormData({
                name: user.name || '',
                email: user.email || '',
                phone: user.phone || '',
                department: user.department || '',
                role: user.role || '',
                avatarUrl: user.avatarUrl || ''
            });
        }
    }, [user]);

    useEffect(() => {
        // Fetch user-specific stats
        const fetchUserStats = async () => {
            if (!user) return;
            try {
                // This would be replaced with actual Firestore queries
                // For now, using mock data
                setUserStats({
                    totalComplaints: 42,
                    resolvedComplaints: 38,
                    pendingComplaints: 4,
                    avgResolutionTime: 3.2
                });
            } catch (error) {
                console.error('Error fetching user stats:', error);
            }
        };
        fetchUserStats();
    }, [user]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        if (!user) return;
        setLoading(true);
        try {
            const userRef = doc(db, 'users', user.uid);
            await updateDoc(userRef, {
                name: formData.name,
                phone: formData.phone,
                department: formData.department,
                updatedAt: Timestamp.now()
            });

            // Update auth user display name
            await updateProfile(auth.currentUser, {
                displayName: formData.name
            });

            toast.success('Profile updated successfully!');
            setIsEditing(false);
        } catch (error) {
            console.error('Error updating profile:', error);
            toast.error('Failed to update profile');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate("/auth");
            toast.success("Logged out successfully!");
        } catch (error) {
            console.error("Logout failed:", error);
            toast.error("Error signing out. Please try again.");
        }
    };

    const handleRefreshCache = async () => {
        try {
            await refreshUser();
            toast.success('Cache refreshed successfully!');
        } catch (error) {
            console.error('Error refreshing cache:', error);
            toast.error('Failed to refresh cache');
        }
    };

    const avatarUrl = formData.avatarUrl || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&q=80&w=150";

    const statCards = [
        {
            label: 'Total Complaints',
            value: userStats.totalComplaints,
            icon: <TrendingUp className="w-5 h-5" />,
            color: 'blue'
        },
        {
            label: 'Resolved',
            value: userStats.resolvedComplaints,
            icon: <CheckCircle className="w-5 h-5" />,
            color: 'green'
        },
        {
            label: 'Pending',
            value: userStats.pendingComplaints,
            icon: <Clock className="w-5 h-5" />,
            color: 'yellow'
        },
        {
            label: 'Avg Resolution',
            value: `${userStats.avgResolutionTime}h`,
            icon: <Activity className="w-5 h-5" />,
            color: 'purple'
        }
    ];

    const activityLogs = [
        {
            id: 1,
            action: 'Resolved complaint #TICKET-1234',
            timestamp: '2 hours ago',
            type: 'completed'
        },
        {
            id: 2,
            action: 'Assigned to technician',
            timestamp: '5 hours ago',
            type: 'assigned'
        },
        {
            id: 3,
            action: 'Created new complaint',
            timestamp: '1 day ago',
            type: 'created'
        }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
            <Navbar user={user} />

            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="flex justify-between items-center">
                    <div className="mb-8">
                        <h1 className="text-3xl font-bold text-gray-800">Profile</h1>
                        <p className="text-gray-500 mt-1">Manage your account settings and preferences</p>
                    </div>

                    <button
                        onClick={handleRefreshCache}
                        className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg hover:from-blue-600 hover:to-indigo-600 transition-all shadow-md hover:shadow-blue-500/25 flex items-center gap-2"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Refresh user
                    </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column - Profile Info */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden sticky top-24">
                            {/* Cover Image */}
                            <div className="h-24 bg-gradient-to-r from-blue-500 to-indigo-600 relative">
                                <button className="absolute bottom-3 right-3 p-1.5 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition">
                                    <Camera className="w-4 h-4 text-white" />
                                </button>
                            </div>

                            {/* Avatar */}
                            <div className="relative px-6 pb-6">
                                <div className="flex flex-col items-center -mt-12">
                                    <div className="relative">
                                        <img
                                            src={avatarUrl}
                                            alt={formData.name || 'User'}
                                            className="w-24 h-24 rounded-full border-4 border-white shadow-lg object-cover"
                                        />
                                        <button className="absolute bottom-0 right-0 p-1.5 bg-blue-600 rounded-full hover:bg-blue-700 transition">
                                            <Camera className="w-3.5 h-3.5 text-white" />
                                        </button>
                                    </div>
                                    <h2 className="mt-3 text-xl font-bold text-gray-800">{formData.name || 'User'}</h2>
                                    <p className="text-sm text-gray-500 capitalize flex items-center gap-1">
                                        <Shield className="w-4 h-4" />
                                        {formData.role || 'No Role'}
                                    </p>
                                    {formData.department && (
                                        <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                                            <Building className="w-3.5 h-3.5" />
                                            {formData.department}
                                        </p>
                                    )}
                                </div>

                                {/* Quick Actions */}
                                <div className="mt-4 space-y-2">
                                    <button
                                        onClick={() => setIsEditing(!isEditing)}
                                        className="w-full px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition flex items-center justify-center gap-2 font-medium"
                                    >
                                        <Edit2 className="w-4 h-4" />
                                        {isEditing ? 'Cancel Editing' : 'Edit Profile'}
                                    </button>
                                    <button
                                        onClick={handleLogout}
                                        className="w-full px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition flex items-center justify-center gap-2 font-medium"
                                    >
                                        <LogOut className="w-4 h-4" />
                                        Sign Out
                                    </button>
                                </div>
                            </div>

                            {/* Contact Info */}
                            <div className="border-t border-gray-100 px-6 py-4">
                                <h3 className="text-sm font-semibold text-gray-700 mb-3">Contact Information</h3>
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                        <Mail className="w-4 h-4 text-gray-400" />
                                        <span>{formData.email || 'No email'}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                        <Phone className="w-4 h-4 text-gray-400" />
                                        <span>{formData.phone || 'No phone'}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                        <Calendar className="w-4 h-4 text-gray-400" />
                                        <span>Joined {new Date().toLocaleDateString()}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Stats & Activity */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Stats Cards */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {statCards.map((stat, idx) => (
                                <div
                                    key={idx}
                                    className="bg-white rounded-2xl shadow-lg p-4 border border-gray-100 transition-all hover:shadow-xl"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`p-2 bg-${stat.color}-50 rounded-xl text-${stat.color}-600`}>
                                            {stat.icon}
                                        </div>
                                        <div>
                                            <p className="text-lg font-bold text-gray-800">{stat.value}</p>
                                            <p className="text-xs text-gray-500 font-medium">{stat.label}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Edit Profile Form */}
                        {isEditing && (
                            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 animate-fade-in">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-gray-800">Edit Profile</h3>
                                    <button
                                        onClick={() => setIsEditing(false)}
                                        className="p-1 hover:bg-gray-100 rounded-lg transition"
                                    >
                                        <X className="w-5 h-5 text-gray-500" />
                                    </button>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                            Full Name
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Enter your name"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                            Email
                                        </label>
                                        <input
                                            type="email"
                                            value={formData.email}
                                            disabled
                                            className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                            Phone Number
                                        </label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Enter phone number"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                            Department
                                        </label>
                                        <input
                                            type="text"
                                            name="department"
                                            value={formData.department}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Enter department"
                                        />
                                    </div>
                                </div>
                                <div className="flex gap-3 mt-4">
                                    <button
                                        onClick={handleSave}
                                        disabled={loading}
                                        className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 font-medium flex items-center gap-2"
                                    >
                                        <Save className="w-4 h-4" />
                                        {loading ? 'Saving...' : 'Save Changes'}
                                    </button>
                                    <button
                                        onClick={() => setIsEditing(false)}
                                        className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition font-medium"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Recent Activity */}
                        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Activity className="w-5 h-5 text-gray-500" />
                                    <h3 className="text-lg font-semibold text-gray-800">Recent Activity</h3>
                                </div>
                                <Link to="/activity" className="text-sm text-blue-600 hover:text-blue-700 transition">
                                    View All
                                </Link>
                            </div>
                            <div className="divide-y divide-gray-100">
                                {activityLogs.map((log) => (
                                    <div key={log.id} className="px-6 py-3 flex items-center justify-between hover:bg-gray-50 transition">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-2 h-2 rounded-full ${log.type === 'completed' ? 'bg-green-500' :
                                                log.type === 'assigned' ? 'bg-blue-500' :
                                                    'bg-yellow-500'
                                                }`} />
                                            <span className="text-sm text-gray-700">{log.action}</span>
                                        </div>
                                        <span className="text-xs text-gray-400">{log.timestamp}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;